import { useEffect } from "react";

import { TLatLngTuple } from "@ryde/shared";

import { decodePolylineOrNull } from "./maplibre-polyline-edit.utils";

/**
 * Keeps the drawn line in step with the geometry that owns it — the course form's `metadata.routePolyline`,
 * falling back to the positions the parent passed in (OSRM preview when there is no real polyline).
 *
 * Suspended while the user is drawing OR editing vertices. Both modes hold their own in-progress copy of the
 * geometry (the editor seeds simplified handles once, at mount), so letting an external update through would
 * replace the line under the handles and leave them pointing at coordinates the layer no longer draws — the
 * user's edit silently lost on the next form change.
 *
 * Manual polyline (`isManualPolylineEdit`) always wins over OSRM: a hand-drawn line often has fewer vertices
 * than a snapped road path, and must not be replaced after Finish.
 */
export const useMapLibrePolylineFormSync = ({
  positions,
  routePolylineFromForm,
  isManualPolylineEdit,
  hasUnsavedStationsChange = false,
  isDrawing,
  isEditing,
  onSyncedPositions,
}: {
  positions: TLatLngTuple[];
  routePolylineFromForm: string | undefined;
  isManualPolylineEdit?: boolean;
  /** When stations were reordered locally, the form polyline is stale until recount/save. */
  hasUnsavedStationsChange?: boolean;
  isDrawing: boolean;
  isEditing: boolean;
  onSyncedPositions: (positions: TLatLngTuple[]) => void;
}): void => {
  useEffect(() => {
    if (isDrawing || isEditing) {
      return;
    }

    // Drag/add/remove updates Recoil first; form `metadata.routePolyline` can still hold the old path.
    if (hasUnsavedStationsChange) {
      onSyncedPositions(positions);
      return;
    }

    const decodedPositions = decodePolylineOrNull(
      routePolylineFromForm,
      "Error decoding routePolyline from form:",
    );

    // Hand-drawn geometry is owned by the form even when it has fewer vertices than OSRM.
    if (isManualPolylineEdit && decodedPositions?.length) {
      onSyncedPositions(decodedPositions);
      return;
    }

    if (decodedPositions && decodedPositions.length > positions.length) {
      onSyncedPositions(decodedPositions);
      return;
    }

    if (positions.length) {
      onSyncedPositions(positions);
      return;
    }

    onSyncedPositions([]);
    // `onSyncedPositions` is a stable `useEvent` handler; listing it would only add noise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    positions,
    routePolylineFromForm,
    isManualPolylineEdit,
    hasUnsavedStationsChange,
    isDrawing,
    isEditing,
  ]);
};
